import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { AppRoutes } from './routes/AppRoutes';
import { isMonitorAvailable, getMonitorApiBaseUrl } from './config/env';
import { Modal } from 'antd';
import { LoginPage } from './pages/LoginPage';
import { ApiError } from './api/types';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        if (failureCount >= 1) {
          return false;
        }
        if (error instanceof ApiError) {
          return !error.status || error.status >= 500;
        }
        return true;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function SessionQueries({ children }: { children: React.ReactNode }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: queryClient.getDefaultOptions() }));
  useEffect(() => () => {
    void client.cancelQueries();
    client.clear();
  }, [client]);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

function SessionApp() {
  const { activeSession, activation } = useAuth();
  const location = useLocation();
  const previous = useRef({ id: activeSession?.id, activation });
  const switched = previous.current.id && activeSession && previous.current.activation !== activation;
  useEffect(() => {
    previous.current = { id: activeSession?.id, activation };
  }, [activeSession?.id, activation]);
  let target = location.pathname;
  if (switched) {
    if (/^\/users\/[^/]+/.test(target)) target = '/users';
    if (/^\/administrators\/[^/]+/.test(target)) target = '/administrators';
    if ((target === '/monitor' && !isMonitorAvailable()) ||
        (target === '/system-alert' && !getMonitorApiBaseUrl())) {
      target = '/home';
    }
  }

  return (
    <SessionQueries key={`${activeSession?.id || 'chooser'}:${activation}`}>
      {activeSession?.needsLogin ? (
        <Modal open centered title="Sign in again" footer={null} closable={false} maskClosable={false} keyboard={false}>
          <LoginPage embedded initialSession={activeSession} />
        </Modal>
      ) : target !== location.pathname ? <Navigate to={target} replace /> : (
        <AppRoutes />
      )}
    </SessionQueries>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SessionApp />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
